// Must run before router and analytics modules capture window.location.
import './Config/app/authUrlPreflight';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import './Config/app/i18n';
import './Config/api/httpClient.config';
import './Domain/style/app.css';
import './Domain/style/legacy.scss';
import { initDatadogRum } from './Config/app/initDatadogRum';
import { initPostHog } from './Config/app/initPostHog';
import { mainRouter } from './Config/app/router/main.router';
import {
  fireAccountCreatedConversion,
  reportActivationResume,
} from './Config/libs/activationConversion';
import { initOpenAiPixel, measureOpenAiConversion } from './Config/libs/openAiPixel';
import { ThemeProvider } from './Domain/components/ThemeProvider';
import { authFragmentStripFailed } from './Domain/utils/authCallbackParams';
import { consumeActivationResumeNext } from './Domain/utils/safeNextPath';

// The Django shell drops a single JSON script with the deployed bundle version +
// environment. Read it once at boot; everything else comes from the bootstrap
// endpoint after authentication.
interface FrontendConfig {
  version: string;
  commit_sha?: string;
  dd_env: string;
}

function readFrontendConfig(): FrontendConfig {
  const el = document.getElementById('frontend-config');
  if (!el?.textContent) return { version: 'unknown', dd_env: '' };
  try {
    return JSON.parse(el.textContent) as FrontendConfig;
  } catch {
    return { version: 'unknown', dd_env: '' };
  }
}

const frontendConfig = readFrontendConfig();

// Strip any MCP-authorize `next` off the boot URL BEFORE analytics/RUM init so its OAuth params
// (state, code_challenge) never enter telemetry; replayed after the activation conversion below.
const activationResume = consumeActivationResumeNext();

// Mirrors GTM_EVENT_TIMEOUT_MS in analytics.ts.
const ACTIVATION_RESUME_NAV_TIMEOUT_MS = 2000;

// --- Datadog RUM (prod only) -------------------------------------------------
// Both SDKs stamp the full href on every event, so neither may boot on a load where the
// login nonce could not be lifted out of the URL.
if (!authFragmentStripFailed()) {
  initDatadogRum(frontendConfig);

  // --- PostHog + OAuth attribution ------------------------------------------
  // OAuth attribution (posthog_oauth_tracking) is handled once by
  // processOAuthTracking() inside initPostHog(), gated on VITE_PUBLIC_POSTHOG_KEY.
  initPostHog(frontendConfig);
}
initOpenAiPixel();

// `?account_activated=1` is set by the backend email-activation endpoint on the
// first successful flip; fire the ad-network conversion once, then strip the
// param so a refresh doesn't re-fire.
const activationUrl = new URL(window.location.href);
if (activationUrl.searchParams.get('account_activated') === '1') {
  const regProps = { event_category: 'User Registration', auth_method: 'email' };
  const hsq = (window._hsq = window._hsq || []);
  hsq.push(['trackCustomBehavioralEvent', { name: 'account_created', properties: regProps }]);
  measureOpenAiConversion('registration_completed');

  activationUrl.searchParams.delete('account_activated');
  window.history.replaceState(
    {},
    '',
    activationUrl.pathname + activationUrl.search + activationUrl.hash,
  );

  reportActivationResume(activationResume, false);
  // Without a carried `next`, the helper falls back to reloading `/` so the server's
  // authenticated redirect chain forwards to /i/{scenario}.
  fireAccountCreatedConversion(
    regProps,
    activationResume.resume,
    ACTIVATION_RESUME_NAV_TIMEOUT_MS,
    activationResume.resume ? () => reportActivationResume(activationResume, true) : undefined,
  );
}

// Preserve the legacy navigation bridge a few callers still use.
window.__reactRouterNavigate = (navigate) => {
  mainRouter.navigate(navigate);
};

// --- Mount -------------------------------------------------------------------
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <RouterProvider router={mainRouter} />
    </ThemeProvider>
  </StrictMode>,
);
